import { FC, Fragment, useState, useEffect } from 'react';
import { Button, Form, message, Modal } from 'antd';
import { Select } from '../Fields/Select';
import { useReschedulePitchMutation } from '../../api/tenders';
import { Loader } from '../Loader/Loader';
import { tenderDataProps } from '../../pages/TenderViewPage/types';
import clock from '../../assets/clock.svg';
import close from '../../assets/RoundedClose.svg';
import { DatePickerInput } from '../Fields/DatePickerInput';
import moment from 'moment';
import Delete from '../Icons/Delete';
import addslotIcon from '../../assets/Calendar, Schedule, Add.svg';

interface ModalProps {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
    data?: tenderDataProps;
    supplierId?: number;
    pitchId?: number;
}

interface TimeObject {
    value: string | null;
    id?: number | string | null;
}

interface TimeSlot {
    date: string;
    time_from: TimeObject | null;
    time_to: TimeObject | null;
}

const emptySlot = (): TimeSlot => ({
    date: '',
    time_from: null,
    time_to: null
});

export const ReschedulePitchModal: FC<ModalProps> = ({
    openModal,
    setOpenModal,
    data,
    setTrigger,
    supplierId,
    pitchId
}) => {
    const [errors, setErrors] = useState<number[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [isConfirmModal, setIsConfirmModal] = useState(false);
    const [slots, setSlots] = useState<TimeSlot[]>([emptySlot(), emptySlot()]);
    const [duration, setDuration] = useState<number>(60);
    const [reschedulePitch, { data: rescheduleData, isLoading, isSuccess, isError, error }] =
        useReschedulePitchMutation();

    const supplier = data?.suppliers?.find((elem) => elem.id === supplierId)?.name;

    useEffect(() => {
        if (isSuccess) {
            message.success(rescheduleData?.message ?? 'Pitch slots updated');
            setOpenModal(false);
            setTrigger((prev: boolean) => !prev);
        }
    }, [isSuccess, rescheduleData, setOpenModal, setTrigger]);

    useEffect(() => {
        if (isError) {
            message.error((error as { data?: { message?: string } })?.data?.message ?? 'Something went wrong');
        }
    }, [isError, error]);

    useEffect(() => {
        if (!data?.phase_tasks?.length) return;
        const pitchTask = data.phase_tasks.find((elem) => elem.action === 'supplier_pitch_availability_form');
        const raw = pitchTask?.duration;
        const n = raw != null && String(raw) !== '' ? Number(raw) : NaN;
        setDuration(Number.isFinite(n) && n > 0 ? n : 60);
    }, [data]);

    function generateTimeArray(): TimeObject[] {
        const timeArray: TimeObject[] = [
            { value: '07:00', id: 1 },
            { value: '07:30', id: 3 },
            { value: '08:00', id: 5 },
            { value: '08:30', id: 7 },
            { value: '09:00', id: 9 },
            { value: '09:30', id: 11 },
            { value: '10:00', id: 13 },
            { value: '10:30', id: 15 },
            { value: '11:00', id: 17 },
            { value: '11:30', id: 19 },
            { value: '12:00', id: 21 },
            { value: '12:30', id: 23 },
            { value: '13:00', id: 25 },
            { value: '13:30', id: 27 },
            { value: '14:00', id: 29 },
            { value: '14:30', id: 31 },
            { value: '15:00', id: 33 },
            { value: '15:30', id: 35 },
            { value: '16:30', id: 38 }
        ];
        return timeArray;
    }

    const timeArray: TimeObject[] = generateTimeArray();

    /** Опции «Time To»: только слоты после выбранного «Time From» + 17:00 */
    const getDependentTimeToOptions = (timeFrom: TimeObject | null): TimeObject[] => {
        if (!timeFrom?.value) return [];
        const idx = timeArray.findIndex((t) => t.value === timeFrom.value);
        if (idx === -1) return [];
        return [...timeArray.slice(idx + 1), { value: '17:00', id: '17:00' }];
    };

    /** Как в PitchForm: конец слота = time_from + duration (мин.) */
    const computeTimeTo = (timeFrom: TimeObject | null, durationMinutes: number): TimeObject | null => {
        if (!timeFrom?.value || !durationMinutes || durationMinutes <= 0) return null;
        const timeStr = String(timeFrom.value).split(' ')[0];
        const [hours, minutes] = timeStr.split(':').map(Number);
        if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
        const timeFromDate = new Date(1970, 0, 1, hours, minutes);
        const timeToDate = new Date(timeFromDate.getTime() + durationMinutes * 60 * 1000);
        const formattedTimeTo = timeToDate.toTimeString().slice(0, 5);
        return timeArray.find((o) => o.value === formattedTimeTo) ?? { value: formattedTimeTo, id: formattedTimeTo };
    };

    useEffect(() => {
        const mins = Number(duration);
        if (!mins || mins <= 0) return;
        setSlots((prev) =>
            prev.map((slot) => {
                if (!slot.time_from?.value) return { ...slot, time_to: null };
                return { ...slot, time_to: computeTimeTo(slot.time_from, mins) };
            })
        );
    }, [duration]);

    const handleFormSubmit = () => {
        if (checkSlotsOverlap(slots)) {
            return;
        }

        const slotsWithErrors: number[] = [];
        slots.forEach((elem, index) => {
            if (!elem.date || !elem.time_from?.value || !elem.time_to?.value) {
                slotsWithErrors.push(index);
            }
        });
        setErrors(slotsWithErrors);
        if (slotsWithErrors.length > 0) {
            return;
        }

        const tender_id = data?.id;

        if (tender_id == null || supplierId == null || pitchId == null) {
            message.error('Missing Matchmaking Journey, supplier or pitch information');
            return;
        }

        const body = {
            slots: slots.map((slot) => ({
                date: moment(slot.date).format('YYYY-MM-DD'),
                time_from: String(slot.time_from?.value ?? '').split(' ')[0],
                time_to: String(slot.time_to?.value ?? '').split(' ')[0]
            }))
        };

        reschedulePitch({
            tender_id,
            supplier_id: supplierId,
            pitch_id: pitchId,
            data: body
        });
    };

    /** Как в PitchForm: пересечение интервалов на одной календарной дате */
    const checkSlotsOverlap = (list: TimeSlot[] | undefined): boolean => {
        if (!list?.length) return false;
        for (let i = 0; i < list.length; i++) {
            for (let j = i + 1; j < list.length; j++) {
                const date1 = new Date(list[i].date);
                const date2 = new Date(list[j].date);

                if (date1.toDateString() !== date2.toDateString()) continue;

                const timeFrom1 = list[i].time_from?.value;
                const timeTo1 = list[i].time_to?.value;
                const timeFrom2 = list[j].time_from?.value;
                const timeTo2 = list[j].time_to?.value;

                if (!timeFrom1 || !timeTo1 || !timeFrom2 || !timeTo2) continue;

                const tf1 = String(timeFrom1).split(' ')[0];
                const tt1 = String(timeTo1).split(' ')[0];
                const tf2 = String(timeFrom2).split(' ')[0];
                const tt2 = String(timeTo2).split(' ')[0];

                if (tt1 > tf2 && tt2 > tf1) {
                    return true;
                }
            }
        }
        return false;
    };

    const deleteSlot = (index: number) => {
        setIsDirty(true);
        const array = [...slots];
        array.splice(index, 1);
        setSlots(array);
    };

    const handleInputChange = (index: number, raw: TimeObject | string | null, name: keyof TimeSlot) => {
        setIsDirty(true);
        const indexToRemove = errors.indexOf(index);
        const newErrors = [...errors];

        if (indexToRemove !== -1) {
            newErrors.splice(indexToRemove, 1);
            setErrors(newErrors);
        }

        setSlots((prevState) =>
            prevState.map((slot, i) => {
                if (i !== index) return slot;

                if (name === 'date') {
                    return { ...slot, date: typeof raw === 'string' ? raw : String(raw ?? '') };
                }

                if (name === 'time_from') {
                    const next: TimeSlot = { ...slot, time_from: raw as TimeObject | null };
                    if (raw == null) {
                        next.time_to = null;
                        return next;
                    }
                    next.time_to = computeTimeTo(raw as TimeObject, Number(duration));
                    return next;
                }

                if (name === 'time_to') {
                    return { ...slot, time_to: raw as TimeObject | null };
                }

                return slot;
            })
        );
    };

    const disabledDate = (current: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const day = new Date(current);
        day.setHours(0, 0, 0, 0);
        return day > today;
    };

    const setSlotsFunc = () => {
        setIsDirty(true);

        setSlots((prevState) => [...prevState, emptySlot()]);
    };

    return (
        <>
            <Modal
                className="modal-supplier-patricipants"
                destroyOnClose
                centered
                open={openModal}
                onOk={() => handleFormSubmit()}
                onCancel={() => {
                    if (isDirty) {
                        setIsConfirmModal(true);
                    } else {
                        setOpenModal(false);
                    }
                }}
                closeIcon={<img src={close} />}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button
                            block
                            key="back"
                            onClick={() => {
                                if (isDirty) {
                                    setIsConfirmModal(true);
                                } else {
                                    setOpenModal(false);
                                }
                            }}
                            className="cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            block
                            key="submit"
                            type="primary"
                            onClick={handleFormSubmit}
                            disabled={checkSlotsOverlap(slots) || isLoading}
                        >
                            {isLoading ? <Loader /> : 'Save'}
                        </Button>
                    </div>
                }
            >
                <div className="modal">
                    <div className="modal__header meeting" style={{ height: '110px' }}>
                        <div className="modal__title">Pitch Reschedule</div>
                        <div className="modal__id">{supplier}</div>
                    </div>

                    <div className="modal__main main" style={{ padding: '24px 56px 43px 56px' }}>
                        <div className="main__subtitle" style={{ margin: '0 0 6px 0' }}>
                            Select at least two available time slots for the supplier to consider
                        </div>

                        <Form onFinish={handleFormSubmit} onChange={() => setIsDirty(true)}>
                            {slots?.map((elem: TimeSlot, index: number) => {
                                return (
                                    <Fragment key={index}>
                                        <div className={`slot ${errors?.includes(index) ? 'error' : ''}`}>
                                            <div className="slot__title">
                                                Slot {index + 1}
                                                {slots?.length > 2 && (
                                                    <span onClick={() => deleteSlot(index)}>
                                                        <Delete fill="#E80A0F" style={{ cursor: 'pointer' }} />
                                                    </span>
                                                )}
                                            </div>
                                            <Form className="slot__form">
                                                <Form.Item className="input-wrapper">
                                                    <DatePickerInput
                                                        onChange={(data: string) =>
                                                            handleInputChange(index, data, 'date')
                                                        }
                                                        value={elem.date}
                                                        required={true}
                                                        placeholder=""
                                                        disabled={false}
                                                        floatLabel="Meeting Date"
                                                        filterDisabled={disabledDate}
                                                    />
                                                </Form.Item>
                                                <div className="slot__row">
                                                    <Form.Item className="input-wrapper pitch-time">
                                                        <Select
                                                            className="time-select"
                                                            setChanged={() => {}}
                                                            onChange={(data: TimeObject | null) =>
                                                                handleInputChange(index, data, 'time_from')
                                                            }
                                                            value={elem.time_from}
                                                            floatLabel="Time From"
                                                            maxLength={60}
                                                            required={true}
                                                            hasError={() => false}
                                                            options={timeArray}
                                                            image={clock}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item className="input-wrapper pitch-time">
                                                        <Select
                                                            disabled={true}
                                                            setChanged={() => {}}
                                                            onChange={(data: TimeObject | null) =>
                                                                handleInputChange(index, data, 'time_to')
                                                            }
                                                            value={elem.time_to}
                                                            floatLabel="Time To"
                                                            maxLength={60}
                                                            required={true}
                                                            hasError={() => false}
                                                            options={
                                                                elem.time_to &&
                                                                !getDependentTimeToOptions(elem.time_from).some(
                                                                    (t) => t.value === elem.time_to?.value
                                                                )
                                                                    ? [
                                                                          elem.time_to,
                                                                          ...getDependentTimeToOptions(elem.time_from)
                                                                      ]
                                                                    : getDependentTimeToOptions(elem.time_from)
                                                            }
                                                            image={clock}
                                                        />
                                                    </Form.Item>
                                                </div>
                                            </Form>
                                        </div>
                                        {errors.includes(index) && (
                                            <div className="error-text" style={{ margin: '-15px 0 10px 0' }}>
                                                These are required fields
                                            </div>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </Form>
                        {checkSlotsOverlap(slots) && (
                            <div
                                className="error-text"
                                style={{ width: '100%', textAlign: 'center', margin: '-8px 0 12px 0' }}
                            >
                                Time slots cannot overlap
                            </div>
                        )}
                        <Button className="modal__duration-item slot" onClick={setSlotsFunc}>
                            <img src={addslotIcon} className="modal__duration-item-image" />
                            Add Slot
                        </Button>
                    </div>
                </div>
            </Modal>
            <Modal
                className="confirmModal"
                centered
                open={isConfirmModal}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsConfirmModal(false)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={() => {
                                setOpenModal(false);
                                setIsConfirmModal(false);
                            }}
                        >
                            Quit
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">Your data won’t be saved! Are you sure you want to quit?</div>
            </Modal>
        </>
    );
};
